import React, { useState, useEffect } from 'react';
import PineconeConnection from '../../../models/Connections/PineconeConnection';
import { 
  Card, 
  CardContent, 
  TextField, 
  Button, 
  Switch, 
  FormControlLabel,
  Divider, 
  Typography,
  Box,
  IconButton,
  Collapse
} from '@mui/material';
import { Database } from 'lucide-react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

interface PineconeConnectionBoxProps {
  pinneconeConnectionData: PineconeConnection;
  onUpdate?: (connection: PineconeConnection) => void;
}

const PineconeConnectionBox: React.FC<PineconeConnectionBoxProps> = ({ pinneconeConnectionData, onUpdate }) => {
  const [data, setData] = useState<PineconeConnection>(pinneconeConnectionData);
  const [indexName, setIndexName] = useState<string>(data.connectionArgs.indexName || '');
  const [namespace, setNamespace] = useState<string>(data.connectionArgs.namespace || '');
  const [k, setK] = useState<number | string>(data.connectionArgs.k || 5);
  const [filter, setFilter] = useState<Record<string, any>>(data.connectionArgs.filter || {});
  const [filterInput, setFilterInput] = useState<string>(JSON.stringify(data.connectionArgs.filter || {}, null, 2));
  const [error, setError] = useState<string>('');
  const [retrieve, setRetrieve] = useState<boolean>(data.connectionArgs.retrieve ?? true);
  const [learn, setLearn] = useState<boolean>(data.connectionArgs.learn ?? false);
  const [isCollapsed, setIsCollapsed] = useState<boolean>(false);

  useEffect(() => {
    const updatedData = new PineconeConnection({
      indexName,
      namespace,
      k: typeof k === 'string' ? parseInt(k, 10) : k,
      filter,
      // retrieve and learn properties removed - not part of PineconeConnectionArgs
    });
    updatedData.id = pinneconeConnectionData.id;
    setData(updatedData);

    if (onUpdate) {
      onUpdate(updatedData);
    }
  }, [indexName, namespace, k, filter, retrieve, learn, pinneconeConnectionData.id, onUpdate]);

  const handleFilterChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setFilterInput(value);

    try {
      const parsedValue = value === 'null' ? null : JSON.parse(value);
      setFilter(parsedValue);
      setError('');
    } catch (err) {
      setError('Invalid JSON format');
    }
  };

  const handleKChange = (increment: number) => {
    setK((prevK) => {
      const currentK = typeof prevK === 'string' ? parseInt(prevK, 10) : prevK;
      const newK = currentK + increment;
      return Math.min(Math.max(newK, 1), 50);
    });
  };

  const handleKInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value, 10);
    if (!isNaN(value)) {
      setK(Math.min(Math.max(value, 1), 50));
    } else if (e.target.value === '') {
      setK('');
    }
  };

  return (
    <Card variant="outlined" sx={{ borderColor: 'purple.300', borderWidth: 1 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton
              onClick={() => setIsCollapsed(!isCollapsed)}
              size="small"
            >
              {isCollapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
            </IconButton>
            <Database size={20} color="#9c27b0" />
            <Typography variant="h6" fontWeight="bold">
              Pinecone Vector Connection
            </Typography>
          </Box>
        </Box>

        <Collapse in={!isCollapsed}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Connection Properties */}
            <Card variant="outlined" sx={{ bgcolor: 'purple.50' }}>
              <CardContent>
                <Typography variant="subtitle2" gutterBottom>
                  Connection Properties
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={retrieve}
                        onChange={(e) => setRetrieve(e.target.checked)}
                        color="primary"
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2" fontWeight="bold">Retrieve</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Enable information retrieval from this connection
                        </Typography>
                      </Box>
                    }
                  />
                  
                  <Divider />
                  
                  <FormControlLabel
                    control={
                      <Switch
                        checked={learn}
                        onChange={(e) => setLearn(e.target.checked)}
                        color="primary"
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2" fontWeight="bold">Learn</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Enable learning and storing new information
                        </Typography>
                      </Box>
                    }
                  />
                </Box>
              </CardContent>
            </Card>

            {/* Connection Configuration */}
            <TextField
              label="Index Name"
              value={indexName}
              onChange={(e) => setIndexName(e.target.value)}
              placeholder="my-index"
              fullWidth
              size="small"
              helperText="Name of the Pinecone index to query"
            />

            <TextField
              label="Namespace"
              value={namespace}
              onChange={(e) => setNamespace(e.target.value)}
              placeholder="default"
              fullWidth
              size="small"
              helperText="Namespace within the index (optional)"
            />

            <Box>
              <Typography variant="body2" fontWeight="medium" gutterBottom>
                Top K Results
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => handleKChange(-1)}
                >
                  -
                </Button>
                <TextField
                  value={k}
                  onChange={handleKInputChange}
                  size="small"
                  sx={{ width: 80 }}
                  inputProps={{ style: { textAlign: 'center' } }}
                />
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => handleKChange(1)}
                >
                  +
                </Button>
              </Box>
              <Typography variant="caption" color="text.secondary">
                Number of similar results to return (1-50)
              </Typography>
            </Box>

            <TextField
              label="Filter (JSON)"
              value={filterInput}
              onChange={handleFilterChange}
              multiline
              rows={4}
              fullWidth
              size="small"
              placeholder='{"category": "documents", "status": "active"}'
              error={!!error}
              helperText={error || "Metadata filter to apply to search results"}
              sx={{ fontFamily: 'monospace' }}
            />
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
};

export default PineconeConnectionBox;